import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { dialogApi } from '@/services/dialog-api';
import { toApiError } from '@/lib/plan-limit';
import type { CreateDialogInput, AddRespondentsInput, RespondentContext } from '@/types/dialog';

// PM-facing hooks

export function useDialogs(projectId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['dialogs', projectId],
    queryFn: async () => {
      const response = await api.listDialogs(projectId);
      return (response.success && response.data) ? response.data : [];
    },
    enabled: !!projectId && (options?.enabled ?? true),
  });
}

export function useCreateDialog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateDialogInput) => {
      const response = await api.createDialog(input);
      if (!response.success || !response.data) {
        throw toApiError(response, 'Failed to create dialog');
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dialogs', data.projectId] });
    },
  });
}

// Idea-capture coach. Get-or-create the project's singleton coach
// dialog (the backend returns the existing live one if present) and hand back
// its token so the in-app coach chat can stream against it. Not invalidating
// ['dialogs'] — coach rows are filtered out of that list server-side.
export function useGetOrCreateCoach() {
  return useMutation({
    mutationFn: async (projectId: string) => {
      const response = await api.createDialog({ projectId, label: 'Idea coach', mode: 'idea-coach' });
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to start the idea coach');
      }
      return response.data;
    },
  });
}

export function useDeleteDialog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, projectId }: { id: string; projectId: string }) => {
      const response = await api.deleteDialog(id, projectId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to close dialog');
      }
      return projectId;
    },
    onSuccess: (projectId) => {
      queryClient.invalidateQueries({ queryKey: ['dialogs', projectId] });
    },
  });
}

// Per-dialog org-framing toggle (step 4). Returning the invalidation promise
// keeps isPending true until the dialogs refetch lands — no flicker-back on
// the optimistic checkbox.
export function useSetDialogOrgFraming() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      projectId,
      personaliseWithOrg,
    }: {
      id: string;
      projectId: string;
      personaliseWithOrg: boolean;
    }) => {
      const response = await api.setDialogOrgFraming(id, projectId, personaliseWithOrg);
      if (!response.success) {
        throw new Error(response.message || 'Failed to update dialog');
      }
      return { projectId };
    },
    onSuccess: ({ projectId }) =>
      queryClient.invalidateQueries({ queryKey: ['dialogs', projectId] }),
  });
}

// Respondent management hooks (PM-facing)

export function useRespondents(dialogId: string, projectId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['respondents', dialogId, projectId],
    queryFn: async () => {
      const response = await api.listRespondents(dialogId, projectId);
      return (response.success && response.data) ? response.data : [];
    },
    enabled: !!dialogId && !!projectId && (options?.enabled ?? true),
  });
}

export function useAddRespondents() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dialogId, respondents, projectId }: AddRespondentsInput & { projectId: string }) => {
      const response = await api.addRespondents(dialogId, { dialogId, respondents }, projectId);
      if (!response.success || !response.data) {
        throw toApiError(response, 'Failed to add respondents');
      }
      return { data: response.data, dialogId, projectId };
    },
    onSuccess: ({ dialogId, projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['respondents', dialogId] });
      // Invites reserve conversations; refresh the project's usage.
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

export function useUpdateRespondentContext() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dialogId, respondentId, projectId, context }: { dialogId: string; respondentId: string; projectId: string; context: RespondentContext }) => {
      const response = await api.updateRespondentContext(dialogId, respondentId, projectId, { context });
      if (!response.success) {
        throw new Error(response.message || 'Failed to update respondent context');
      }
      return { dialogId };
    },
    onSuccess: ({ dialogId }) => {
      queryClient.invalidateQueries({ queryKey: ['respondents', dialogId] });
    },
  });
}

export function useResendInvite() {
  return useMutation({
    mutationFn: async ({ dialogId, respondentId, projectId }: { dialogId: string; respondentId: string; projectId: string }) => {
      const response = await api.resendInvite(dialogId, respondentId, projectId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to resend invite');
      }
    },
  });
}

export function useRevokeRespondent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ dialogId, respondentId, projectId }: { dialogId: string; respondentId: string; projectId: string }) => {
      const response = await api.revokeRespondent(dialogId, respondentId, projectId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to revoke respondent');
      }
      return { dialogId, projectId };
    },
    onSuccess: ({ dialogId, projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['respondents', dialogId] });
      // Withdrawing an invite nobody opened releases a conversation.
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
    },
  });
}

// Conversation session hooks (PM-facing)

export function useDialogSessions(dialogId: string, projectId: string) {
  return useQuery({
    queryKey: ['dialog-sessions', dialogId, projectId],
    queryFn: async () => {
      const response = await api.listDialogSessions(dialogId, projectId);
      return response.success && response.data ? response.data : [];
    },
    enabled: !!dialogId && !!projectId,
  });
}

export function useSessionDraft(dialogId: string, actorId: string, projectId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['dialog-session-draft', dialogId, actorId, projectId],
    queryFn: async () => {
      const response = await api.getDialogSessionDraft(dialogId, actorId, projectId);
      return response.success && response.data ? response.data.draft : null;
    },
    enabled,
  });
}

export function usePromoteSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ dialogId, actorId, projectId }: { dialogId: string; actorId: string; projectId: string }) => {
      const response = await api.promoteDialogSession(dialogId, actorId, projectId);
      if (!response.success) throw new Error(response.message || 'Failed to add to graph');
      return { dialogId, projectId };
    },
    onSuccess: ({ dialogId, projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['dialog-sessions', dialogId, projectId] });
      // New nodes should appear on the canvas/peek without a manual refresh —
      // matches the invalidation key used by the synthesis/capabilities promote hooks.
      queryClient.invalidateQueries({ queryKey: ['canvas', projectId] });
    },
  });
}

// Respondent-facing hooks

export function useDialogSession(token: string, clientSessionId?: string) {
  // clientSessionId is provided by the caller (Dialog.tsx) so the value is
  // stable across renders and shared with DialogChat. Without that single
  // source of truth, independent computations could diverge in localStorage-
  // blocked browsers and split persistence across two actor ids per visit.
  return useQuery({
    queryKey: ['dialog-session', token, clientSessionId],
    queryFn: async () => {
      // clientSessionId is the actorId for anonymous respondents on the
      // server side. Always passed — for invited respondents the server
      // ignores it in favour of respondent.id, so there's no downside.
      const response = await dialogApi.getDialogByToken(token, clientSessionId);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Dialog not found');
      }
      return response.data;
    },
    enabled: !!token,
    retry: false,
  });
}

export function useDialogBatchSubmit(token: string) {
  return useMutation({
    mutationFn: async (data: Parameters<typeof dialogApi.batchSubmit>[1]) => {
      const response = await dialogApi.batchSubmit(token, data);
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Batch submit failed');
      }
      return response.data;
    },
  });
}
